import { useCallback, useState } from 'react';

import { DocTypeOption } from '@/features/DocumentSearch/api';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const MAX_SIMILAR_TYPES = 5;
// Shorter existing codes ("wo") sit inside too many unrelated words to count as similar.
const MIN_CONTAINED_LENGTH = 4;

const normalise = (text: string) => text.trim().toLowerCase();

/** The option whose value or label equals `name`, ignoring case and surrounding space. */
export function findExactDocType(
    name: string,
    options: DocTypeOption[],
): DocTypeOption | undefined {
    const wanted = normalise(name);
    return options.find(
        (o) => normalise(o.value) === wanted || normalise(o.label) === wanted,
    );
}

/** Existing types whose value or label contains `name`, or is a longer word inside it, case-insensitively. */
export function similarDocTypes(
    name: string,
    options: DocTypeOption[],
): DocTypeOption[] {
    const wanted = normalise(name);
    if (wanted === '') return [];
    return options
        .filter((o) =>
            [normalise(o.value), normalise(o.label)].some(
                (text) =>
                    text.includes(wanted) ||
                    (text.length >= MIN_CONTAINED_LENGTH &&
                        wanted.includes(text)),
            ),
        )
        .slice(0, MAX_SIMILAR_TYPES);
}

/**
 * Gate for the split editors' "create a new type" action: an existing type is
 * applied straight away, a new label waits for the returned dialog's Create.
 * Render `dialog` once next to the combobox(es) that call `requestCreate`.
 */
export function useConfirmNewDocType(
    options: DocTypeOption[],
    testIdPrefix: string,
) {
    const [pending, setPending] = useState<{
        name: string;
        apply: (value: string) => void;
    } | null>(null);

    const requestCreate = useCallback(
        (name: string, apply: (value: string) => void) => {
            const trimmed = name.trim();
            if (trimmed === '') return;
            const existing = findExactDocType(trimmed, options);
            if (existing) {
                apply(existing.value);
                return;
            }
            setPending({ name: trimmed, apply });
        },
        [options],
    );

    const similar = pending ? similarDocTypes(pending.name, options) : [];

    const dialog = (
        <AlertDialog
            open={pending !== null}
            onOpenChange={(open) => {
                if (!open) setPending(null);
            }}
        >
            <AlertDialogContent data-testid={`${testIdPrefix}__newTypeDialog`}>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        Create new type &quot;{pending?.name}&quot;?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        This label is not in the list of document types. It is
                        kept as written on this file&apos;s splits.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                {similar.length > 0 && (
                    <div
                        className="text-sm"
                        data-testid={`${testIdPrefix}__newTypeSimilar`}
                    >
                        <p className="text-muted-foreground">
                            Similar existing types:
                        </p>
                        <ul className="mt-2 flex flex-col gap-1">
                            {similar.map((o) => (
                                <li key={o.value}>
                                    <button
                                        type="button"
                                        className="underline"
                                        onClick={() => {
                                            pending?.apply(o.value);
                                            setPending(null);
                                        }}
                                    >
                                        {o.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <AlertDialogFooter>
                    <AlertDialogCancel
                        data-testid={`${testIdPrefix}__newTypeCancel`}
                    >
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={() => {
                            pending?.apply(pending.name);
                            setPending(null);
                        }}
                        data-testid={`${testIdPrefix}__newTypeCreate`}
                    >
                        Create
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );

    return { requestCreate, dialog };
}
